import React, { useEffect, useState } from "react";
import {
  FileText, FilePlus, Lock, Eye, Users, Globe2, Download, X, Edit3,
  Trash2, Star, Clock, ChevronDown, Upload, Loader2, AlertCircle,
  MessageSquare, ClipboardList,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";
import { usePortalLang } from "../../lib/PortalLanguageContext";

const VIS_META = {
  internal: { l: "Internal", icon: Lock, cls: "bg-[#1C110C] text-[#FAF5EC]" },
  client: { l: "Client", icon: Users, cls: "bg-[#D4A963] text-[#1C110C]" },
  shared: { l: "Shared", icon: Globe2, cls: "bg-[#3F6E2C] text-[#FAF5EC]" },
};

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
const MAX_UPLOAD_MB = 100;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function fileIconType(name = "", contentType = "") {
  const ext = name.split(".").pop()?.toLowerCase();
  if (contentType.startsWith("image/")) return "Image";
  if (["pdf"].includes(ext)) return "PDF";
  if (["xlsx", "xls", "xlsm", "csv"].includes(ext)) return "Sheet";
  if (["doc", "docx"].includes(ext)) return "Doc";
  if (["dwg", "dxf"].includes(ext)) return "CAD";
  if (["ppt", "pptx"].includes(ext)) return "Slides";
  return (ext || "File").toUpperCase();
}

function VisibilityBadge({ visibility }) {
  const v = VIS_META[visibility] || VIS_META.internal;
  const Icon = v.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${v.cls}`}>
      <Icon size={9} /> {v.l}
    </span>
  );
}

/* ---------- Upload modal ---------- */
function UploadModal({ slug, categories, preselectCategory, onClose, onUploaded, restricted }) {
  const availableCats = (categories || []).filter((c) => !restricted || !c.is_financial);
  const [file, setFile] = useState(null);
  const [docCat, setDocCat] = useState(preselectCategory || "contract");
  const [visibility, setVisibility] = useState("internal");
  const [isFinal, setIsFinal] = useState(true);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (restricted && availableCats.length && !availableCats.some((c) => c.value === docCat)) {
      setDocCat(availableCats[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restricted, categories]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Choose a file first."); return; }
    if (file.size > MAX_UPLOAD_BYTES) {
      setErr(`File size exceeds the ${MAX_UPLOAD_MB} MB limit.`);
      return;
    }
    setBusy(true); setErr("");
    try {
      await portalApi.uploadKeyDocument(slug, {
        file, doc_category: docCat,
        visibility: restricted ? "shared" : visibility,
        is_final: restricted ? false : isFinal,
        caption: caption || undefined,
      });
      onUploaded && onUploaded();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const cls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-[12px] focus:outline-none focus:border-[#6E1F2C]";
  const lbl = "block text-[10px] tracking-[0.26em] uppercase text-[#7A6B5F] mb-1";
  const selectedCat = availableCats.find((c) => c.value === docCat);

  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="bg-white w-full max-w-[600px] border border-[#6E1F2C]/20 my-6" onClick={(e) => e.stopPropagation()} data-testid="portal-keydocs-upload-modal">
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
          <h3 className="font-serif-display text-xl text-[#1C110C]">Upload key document</h3>
          <button type="button" onClick={onClose} className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <label htmlFor="portal-keydoc-file" className="border-2 border-dashed border-[#6E1F2C]/25 hover:border-[#6E1F2C] block p-6 text-center cursor-pointer transition-colors">
            <Upload className="mx-auto text-[#6E1F2C] mb-2" size={26} />
            {file ? (
              <>
                <p className="text-sm text-[#1C110C]">{file.name}</p>
                <p className="text-[10px] text-[#7A6B5F] mt-1">{fmtSize(file.size)}</p>
              </>
            ) : (
              <p className="text-sm text-[#1C110C]">Click to choose a file (PDF, DWG, XLSX, DOCX…)</p>
            )}
            <p className="text-[10px] text-[#7A6B5F] mt-1">Max {MAX_UPLOAD_MB} MB · large drawings, BOQs and contracts welcome</p>
            <input id="portal-keydoc-file" data-testid="portal-keydocs-file" type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Category</label>
              <select data-testid="portal-keydocs-category-select" value={docCat} onChange={(e) => setDocCat(e.target.value)} className={cls}>
                {availableCats.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}{c.is_financial ? " 🔒" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={lbl}>Visibility</label>
              {restricted ? (
                <p data-testid="portal-keydocs-visibility-fixed" className="text-[11px] text-[#3E2A22] bg-[#FAF5EC] border border-[#6E1F2C]/10 px-3 py-2">
                  <Users size={10} className="inline mr-1 text-[#6E1F2C]" />
                  Visible to the Time Furniture team &amp; client-side members
                </p>
              ) : (
                <select data-testid="portal-keydocs-visibility-select" value={visibility} onChange={(e) => setVisibility(e.target.value)} className={cls}>
                  <option value="internal">Internal — admin / PM / site engineer only</option>
                  <option value="client">Client visible — also visible to client roles</option>
                  <option value="shared">Shared — all project members</option>
                </select>
              )}
              {!restricted && selectedCat?.is_financial && visibility !== "internal" && (
                <p className="text-[10px] text-[#A6822F] mt-1 italic">
                  <Lock size={9} className="inline mr-0.5" /> Financial category — client will only see this if the matching share-with-client toggle is enabled in Financials &gt; Settings.
                </p>
              )}
            </div>
          </div>
          <div>
            <label className={lbl}>Caption / version note</label>
            <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} className={cls} placeholder="e.g. Rev C — issued for construction" data-testid="portal-keydocs-caption" />
          </div>
          {restricted ? (
            <p data-testid="portal-keydocs-review-note" className="text-[11px] text-[#7A6B5F] italic">
              <Star size={11} className="inline mr-1 text-[#A6822F]" />
              Uploaded for company review — the Time Furniture team marks final versions.
            </p>
          ) : (
            <label className="flex items-center gap-2 text-[12px] text-[#1C110C] cursor-pointer">
              <input type="checkbox" data-testid="portal-keydocs-is-final" checked={isFinal} onChange={(e) => setIsFinal(e.target.checked)} className="accent-[#6E1F2C]" />
              <Star size={13} className={isFinal ? "text-[#A6822F]" : "text-[#7A6B5F]"} />
              Mark as the <strong>Final / Current</strong> version (any previous final in this category will move to history)
            </label>
          )}
          {err && <p className="text-[11px] text-[#A3141A]" data-testid="portal-keydocs-error">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-[#6E1F2C]/10 flex items-center justify-end gap-2 bg-[#FAF5EC]/40">
          <button type="button" onClick={onClose} className="text-[10px] tracking-[0.26em] uppercase text-[#7A6B5F] px-4 py-2">Cancel</button>
          <button type="submit" disabled={busy} data-testid="portal-keydocs-upload-submit" className="bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-5 py-2.5 text-[10px] tracking-[0.26em] uppercase disabled:opacity-60 inline-flex items-center gap-2">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {busy ? "Uploading" : "Upload"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- Per-doc row ---------- */
function DocRow({ d, canEdit, onChanged, isHistory }) {
  const [busy, setBusy] = useState(false);
  const markFinal = async () => {
    setBusy(true);
    try { await portalApi.patchKeyDocument(d.id, { is_final: true }); onChanged && onChanged(); }
    catch { /* noop */ } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${d.filename}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await portalApi.deleteKeyDocument(d.id); onChanged && onChanged(); }
    catch { /* noop */ } finally { setBusy(false); }
  };
  return (
    <div
      data-testid={`portal-keydoc-row-${d.id}`}
      className={`flex items-center gap-3 p-3 ${isHistory ? "bg-[#FAF5EC]/40 border border-[#6E1F2C]/10" : "bg-white border border-[#D4A963]/40"}`}
    >
      <div className={`w-11 h-11 ${isHistory ? "bg-[#7A6B5F]/10 text-[#7A6B5F]" : "bg-[#D4A963]/20 text-[#A6822F]"} flex items-center justify-center font-mono text-[10px] font-medium shrink-0`}>
        {fileIconType(d.filename, d.content_type || "")}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          {d.is_final && (
            <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#D4A963] text-[#1C110C] inline-flex items-center gap-1">
              <Star size={9} /> Final · v{d.version}
            </span>
          )}
          {!d.is_final && d.source === "client_submission" && (
            <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#A6822F]/15 text-[#A6822F]">
              Client submission · v{d.version}
            </span>
          )}
          {!d.is_final && d.source !== "client_submission" && (
            <span className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F]">v{d.version}</span>
          )}
          <VisibilityBadge visibility={d.visibility} />
        </div>
        <p className="text-[13px] text-[#1C110C] font-medium truncate">{d.filename}</p>
        {d.caption && <p className="text-[11px] text-[#7A6B5F] truncate italic">{d.caption}</p>}
        <p className="text-[10px] text-[#7A6B5F] mt-0.5">
          <Clock size={9} className="inline mr-0.5" />
          {fmtDate(d.uploaded_at)} · {d.uploaded_by_name || "—"} · {fmtSize(d.size)}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <a
          href={d.url} target="_blank" rel="noopener noreferrer"
          data-testid={`portal-keydoc-view-${d.id}`}
          className="p-2 border border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white"
          title="View"
        >
          <Eye size={12} />
        </a>
        <a
          href={d.url} download={d.filename}
          data-testid={`portal-keydoc-download-${d.id}`}
          className="p-2 border border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white"
          title="Download"
        >
          <Download size={12} />
        </a>
        {canEdit && isHistory && (
          <button
            type="button" onClick={markFinal} disabled={busy}
            data-testid={`portal-keydoc-promote-${d.id}`}
            className="p-2 border border-[#D4A963] text-[#A6822F] hover:bg-[#D4A963] hover:text-[#1C110C] disabled:opacity-50"
            title="Mark as the current Final version"
          >
            <Star size={12} />
          </button>
        )}
        {canEdit && (
          <button
            type="button" onClick={remove} disabled={busy}
            data-testid={`portal-keydoc-delete-${d.id}`}
            className="p-2 border border-[#A3141A]/40 text-[#A3141A] hover:bg-[#A3141A] hover:text-white disabled:opacity-50"
            title="Delete"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- Main tab ---------- */
export function KeyDocumentsTab({ slug, onAddComment, onAddUpdate, canAddUpdate }) {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadCat, setUploadCat] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});

  useEffect(() => {
    let alive = true;
    portalApi.getKeyDocuments(slug)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message); });
    portalApi.getKeyDocCategories(slug)
      .then((d) => { if (alive) setCats(d.categories || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug, refreshKey]);

  const reload = () => setRefreshKey((k) => k + 1);

  if (err) return <div className="text-[12px] text-[#A3141A]" data-testid="portal-keydocs-error-state">{err}</div>;
  if (!data) return <div className="text-[11px] text-[#7A6B5F] py-6">Loading documents…</div>;

  const sections = data.sections || [];
  const canEdit = !!data.viewer_can_edit;
  const canUpload = canEdit || !!data.viewer_can_upload;
  const filtered = filter
    ? sections.filter((s) => s.category === filter || s.label.toLowerCase().includes(filter.toLowerCase()))
    : sections;

  return (
    <div data-testid="portal-keydocs-tab" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
            <FileText size={11} /> Final files & key documents
          </p>
          <h2 className="font-serif-display text-2xl text-[#1C110C] mt-0.5">Latest approved versions</h2>
          {data.last_updated && (
            <p className="text-[11px] text-[#7A6B5F] mt-1">Last updated {fmtDate(data.last_updated)}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onAddComment && (
            <button
              type="button"
              onClick={onAddComment}
              data-testid="portal-keydocs-add-comment-btn"
              className="border border-[#6E1F2C]/30 text-[#6E1F2C] hover:border-[#6E1F2C] hover:text-[#56151F] px-4 py-2.5 text-[10px] tracking-[0.26em] uppercase inline-flex items-center gap-2"
            >
              <MessageSquare size={13} /> Add comment
            </button>
          )}
          {canAddUpdate && onAddUpdate && (
            <button
              type="button"
              onClick={onAddUpdate}
              data-testid="portal-keydocs-add-update-btn"
              className="border border-[#6E1F2C]/30 text-[#6E1F2C] hover:border-[#6E1F2C] hover:text-[#56151F] px-4 py-2.5 text-[10px] tracking-[0.26em] uppercase inline-flex items-center gap-2"
            >
              <ClipboardList size={13} /> Add daily update
            </button>
          )}
          {canUpload && (
            <button
              type="button"
              onClick={() => { setUploadCat(null); setShowUpload(true); }}
              data-testid="portal-keydocs-upload-btn"
              className="bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-4 py-2.5 text-[10px] tracking-[0.26em] uppercase inline-flex items-center gap-2"
            >
              <FilePlus size={13} /> Upload document
            </button>
          )}
        </div>
      </div>

      {/* Category filter chips */}
      {sections.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <button
            type="button" onClick={() => setFilter("")}
            data-testid="portal-keydocs-filter-all"
            className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border ${filter === "" ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "bg-white text-[#6E1F2C] border-[#6E1F2C]/25"}`}
          >
            All
          </button>
          {sections.map((s) => (
            <button
              key={s.category} type="button" onClick={() => setFilter(s.category)}
              data-testid={`portal-keydocs-filter-${s.category}`}
              className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border ${filter === s.category ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "bg-white text-[#6E1F2C] border-[#6E1F2C]/25"}`}
            >
              {s.label} <span className="ml-1 opacity-60">({s.total})</span>
            </button>
          ))}
        </div>
      )}

      {/* Sections */}
      {filtered.length === 0 ? (
        <div data-testid="portal-keydocs-empty" className="bg-white border border-dashed border-[#6E1F2C]/25 p-10 text-center">
          <FileText className="mx-auto text-[#D4A963] mb-3" size={28} />
          <p className="text-sm text-[#1C110C]">No key documents uploaded yet.</p>
          {canUpload && (
            <p className="text-[11px] text-[#7A6B5F] mt-1">Use &ldquo;Upload document&rdquo; to add the first approved file.</p>
          )}
        </div>
      ) : (
        filtered.map((s) => {
          const expanded = expandedCats[s.category];
          return (
            <section key={s.category} data-testid={`portal-keydocs-section-${s.category}`}>
              <div className="flex items-end justify-between mb-2 flex-wrap gap-2">
                <div>
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
                    {s.is_financial && <Lock size={10} className="text-[#A6822F]" />}
                    {s.label}
                  </p>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => { setUploadCat(s.category); setShowUpload(true); }}
                    data-testid={`portal-keydocs-section-upload-${s.category}`}
                    className="text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C] hover:text-[#56151F] border border-[#6E1F2C]/25 hover:border-[#6E1F2C] px-2 py-1 inline-flex items-center gap-1"
                  >
                    <FilePlus size={10} /> New version
                  </button>
                )}
              </div>
              {s.final && <DocRow d={s.final} canEdit={canEdit} onChanged={reload} isHistory={false} />}
              {s.submissions?.length > 0 && (
                <div className="mt-2 space-y-2" data-testid={`portal-keydocs-submissions-${s.category}`}>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[#A6822F]">
                    Client submissions — pending company review
                  </p>
                  {s.submissions.map((d) => (
                    <DocRow key={d.id} d={d} canEdit={canEdit} onChanged={reload} isHistory={false} />
                  ))}
                </div>
              )}
              {s.history?.length > 0 && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setExpandedCats((cur) => ({ ...cur, [s.category]: !cur[s.category] }))}
                    data-testid={`portal-keydocs-history-toggle-${s.category}`}
                    className="text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F] hover:text-[#1C110C] inline-flex items-center gap-1"
                  >
                    <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                    {s.history.length} earlier {s.history.length === 1 ? "version" : "versions"}
                  </button>
                  {expanded && (
                    <div className="mt-2 space-y-2 pl-3 border-l-2 border-[#6E1F2C]/15">
                      {s.history.map((h) => (
                        <DocRow key={h.id} d={h} canEdit={canEdit} onChanged={reload} isHistory={true} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })
      )}

      {showUpload && (
        <UploadModal
          slug={slug}
          categories={cats}
          preselectCategory={uploadCat}
          restricted={!canEdit}
          onClose={() => setShowUpload(false)}
          onUploaded={reload}
        />
      )}
    </div>
  );
}

/* ---------- Overview compact card ---------- */
export function KeyDocumentsCard({ slug, onOpenTab }) {
  const { t } = usePortalLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    portalApi.getKeyDocuments(slug)
      .then(setData)
      .catch(() => setData({ sections: [] }));
  }, [slug]);
  if (!data) {
    return <div className="bg-white border border-[#6E1F2C]/12 h-32 animate-pulse" />;
  }
  const sections = data.sections || [];
  // Pick the top categories to surface — match user's spec.
  const PRIORITY = ["gantt", "boq", "payment_milestone", "drawings", "contract", "authority_approval"];
  const visible = PRIORITY
    .map((cat) => sections.find((s) => s.category === cat && s.final))
    .filter(Boolean);
  return (
    <section
      data-testid="portal-overview-keydocs-card"
      className="bg-white border border-[#6E1F2C]/15 p-5"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
            <FileText size={11} /> {t("hero.keyDocuments")}
          </p>
          <h3 className="font-serif-display text-lg text-[#1C110C] mt-0.5">{t("hero.quickAccess")}</h3>
        </div>
        <button
          type="button"
          onClick={onOpenTab}
          data-testid="portal-overview-keydocs-open"
          className="text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C] hover:text-[#56151F] border border-[#6E1F2C]/25 hover:border-[#6E1F2C] px-2 py-1"
        >
          {t("hero.openFiles")} →
        </button>
      </div>
      {visible.length === 0 ? (
        <p className="text-[11px] text-[#7A6B5F] italic" data-testid="portal-overview-keydocs-empty">
          {t("file.keyDocs.empty")}
        </p>
      ) : (
        <div className="space-y-1.5">
          {visible.map((s) => {
            const d = s.final;
            return (
              <div key={s.category} className="flex items-center gap-2 group">
                <div className="w-7 h-7 bg-[#FAF5EC] border border-[#6E1F2C]/15 flex items-center justify-center text-[#6E1F2C] font-mono text-[8px] shrink-0">
                  {fileIconType(d.filename, d.content_type || "").slice(0, 4)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] tracking-[0.18em] uppercase text-[#7A6B5F]">{s.label}</p>
                  <p className="text-[12px] text-[#1C110C] truncate group-hover:text-[#6E1F2C]">{d.filename}</p>
                </div>
                <VisibilityBadge visibility={d.visibility} />
                <a
                  href={d.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`portal-overview-keydoc-view-${s.category}`}
                  className="p-1.5 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white"
                  title="View"
                >
                  <Eye size={12} />
                </a>
                <a
                  href={d.url} download={d.filename}
                  data-testid={`portal-overview-keydoc-download-${s.category}`}
                  className="p-1.5 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white"
                  title="Download"
                >
                  <Download size={12} />
                </a>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
